"use client";

import { useCallback, useEffect, useState } from "react";

// ──────────────────────────────────────────────────────────────────────────
// useProfile — 자비스 사용자 프로필 (이름 + 이모지)
// localStorage(`jarvis.profile`)에 저장. SSR 안전(mounted 체크).
//
// CurrentUser(user-provider.tsx)는 "이 기기 사용자(suhyeon | wife)" 구분용,
// Profile은 자비스 채팅에 함께 보내는 "표시용 프로필"이다. 둘은 별개.
// ──────────────────────────────────────────────────────────────────────────

export type Profile = {
  id: string;
  name: string;
  emoji: string;
  createdAt: string;
};

export const PROFILE_STORAGE_KEY = "jarvis.profile";

const PROFILE_CHANGE_EVENT = "jarvis:profile-change";

function readProfile(): Profile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Profile>;
    if (
      parsed &&
      typeof parsed.id === "string" &&
      typeof parsed.name === "string" &&
      typeof parsed.emoji === "string" &&
      typeof parsed.createdAt === "string"
    ) {
      return parsed as Profile;
    }
    return null;
  } catch {
    return null;
  }
}

export function useProfile() {
  const [profile, setProfileState] = useState<Profile | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setProfileState(readProfile());
    setMounted(true);

    const onChange = () => setProfileState(readProfile());
    const onStorage = (e: StorageEvent) => {
      if (e.key === PROFILE_STORAGE_KEY) setProfileState(readProfile());
    };
    window.addEventListener(PROFILE_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(PROFILE_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setProfile = useCallback((next: Profile) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(next));
    } catch {}
    setProfileState(next);
    // dispatchEvent를 다음 microtask로 — 다른 컴포넌트의 setState가 같은 render cycle에 끼어드는 거 방지
    queueMicrotask(() => {
      window.dispatchEvent(new Event(PROFILE_CHANGE_EVENT));
    });
  }, []);

  const clearProfile = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.removeItem(PROFILE_STORAGE_KEY);
    } catch {}
    setProfileState(null);
    queueMicrotask(() => {
      window.dispatchEvent(new Event(PROFILE_CHANGE_EVENT));
    });
  }, []);

  return {
    profile,
    setProfile,
    clearProfile,
    isLoading: !mounted,
  };
}
